import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Avatar,
  Badge,
  Breadcrumbs,
  Group,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { LoadingSuspense, useDatasetGroups } from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo } from "react";

import { api } from "@/api.ts";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/admin/products/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`Products`,
      },
    ],
  }),
});

type ProductRow = {
  id: number;
  name: string;
  type: "property-search" | "extraction";
  price: number;
  price_type: "fixed" | "per-object" | "per-area";
  user_category: "internal" | "external";
  customer_category: "geodatasamverkan" | "other" | null;
  customer_id: number | null;
  customer: { name: string } | null;
};

function usePriceTypeSuffix(): Record<ProductRow["price_type"], string> {
  const { t } = useLingui();
  return {
    fixed: "",
    "per-object": "/" + t`object`,
    "per-area": "/" + t`area`,
  };
}

const typeLabels: Record<ProductRow["type"], React.ReactNode> = {
  "property-search": <Trans>Property Search</Trans>,
  extraction: <Trans>Extraction</Trans>,
};

const ProductTypeTable: FC<{
  type: ProductRow["type"];
  rows: Array<ProductRow>;
}> = ({ type, rows }) => {
  const datasetGroups = useDatasetGroups();
  const { i18n } = useLingui();
  const priceTypeSuffix = usePriceTypeSuffix();

  const columns = useMemo(() => {
    const names = Array.from(new Set(rows.map((r) => r.name)));
    return names
      .map((name) => ({
        name,
        datasetGroup: datasetGroups.find((dg) => dg.name === name),
      }))
      .sort((a, b) =>
        (a.datasetGroup?.label ?? a.name).localeCompare(
          b.datasetGroup?.label ?? b.name,
        ),
      );
  }, [rows, datasetGroups]);

  const rowGroups = useMemo(() => {
    const map = new Map<
      string,
      {
        user_category: ProductRow["user_category"];
        customer_category: ProductRow["customer_category"];
        customer: ProductRow["customer"];
        cells: Map<string, ProductRow>;
      }
    >();
    for (const r of rows) {
      const key = `${r.user_category}|${r.customer_category ?? ""}|${r.customer_id ?? ""}`;
      let entry = map.get(key);
      if (!entry) {
        entry = {
          user_category: r.user_category,
          customer_category: r.customer_category,
          customer: r.customer,
          cells: new Map(),
        };
        map.set(key, entry);
      }
      entry.cells.set(r.name, r);
    }
    return Array.from(map.values());
  }, [rows]);

  return (
    <Stack gap="xs">
      <Title order={4}>{typeLabels[type]}</Title>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Trans>User category</Trans>
            </Table.Th>
            <Table.Th>
              <Trans>Customer</Trans>
            </Table.Th>
            {columns.map(({ name, datasetGroup }) => (
              <Table.Th key={name}>
                <Group gap="xs" wrap="nowrap">
                  {datasetGroup && (
                    <Avatar color="green.7" size="sm" mr="sm">
                      <FontAwesomeIcon icon={datasetGroup.icon} />
                    </Avatar>
                  )}
                  <Text fz="sm">{datasetGroup?.label ?? name}</Text>
                </Group>
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rowGroups.map((group, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                <Badge
                  color={group.user_category === "internal" ? "green" : "gray"}
                  variant="light"
                >
                  {group.user_category === "internal" ? (
                    <Trans context="user category">Internal</Trans>
                  ) : (
                    <Trans context="user category">External</Trans>
                  )}
                </Badge>
              </Table.Td>
              <Table.Td>
                {group.customer_category ? (
                  <Badge
                    color={
                      group.customer_category === "geodatasamverkan"
                        ? "green"
                        : "gray"
                    }
                    variant="light"
                  >
                    {group.customer_category === "geodatasamverkan" ? (
                      <Trans context="customer category">
                        Geodatasamverkan
                      </Trans>
                    ) : (
                      <Trans context="customer category">Other</Trans>
                    )}
                  </Badge>
                ) : (
                  (group.customer?.name ?? "-")
                )}
              </Table.Td>
              {columns.map(({ name }) => {
                const product = group.cells.get(name);
                return (
                  <Table.Td key={name}>
                    {product
                      ? `${i18n.number(product.price, {
                          style: "currency",
                          currency: "SEK",
                          maximumFractionDigits: 0,
                        })}${priceTypeSuffix[product.price_type]}`
                      : "-"}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
};

const Products: FC = () => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(() => api.from("product").select("*,customer(name)"), []),
  );

  const byType = useMemo(() => {
    const map = new Map<ProductRow["type"], Array<ProductRow>>();
    for (const d of data as Array<ProductRow>) {
      const list = map.get(d.type) ?? [];
      list.push(d);
      map.set(d.type, list);
    }
    return map;
  }, [data]);

  return (
    <Stack gap="xl">
      {Array.from(byType.entries()).map(([type, rows]) => (
        <ProductTypeTable key={type} type={type} rows={rows} />
      ))}
    </Stack>
  );
};

function RouteComponent() {
  return (
    <Stack gap="lg">
      <Breadcrumbs>
        <Text>
          <Trans>Admin</Trans>
        </Text>
        <Text>
          <Trans>Products</Trans>
        </Text>
      </Breadcrumbs>

      <LoadingSuspense>
        <Products />
      </LoadingSuspense>
    </Stack>
  );
}
